// Kit de impresión reutilizable. Encabezado de marca (logo + empresa) que sale
// SOLO al imprimir, y un contenedor `Imprimible` que, vía @media print, oculta
// todo el chrome (sidebar/tabs/filtros) y deja solo el documento.
//
// Uso:
//   <PrintButton />                      // botón "Imprimir" (oculto al imprimir)
//   <Imprimible titulo="Libro Mayor">    // lo único que se imprime
//     ...contenido del documento...
//   </Imprimible>
import { ReactNode } from 'react';
import { Printer } from 'lucide-react';
import { useAuth } from '@/lib/hooks';
import { Button } from '@/components/ui/Button';
import { LOCALE } from '@/lib/utils/regional';

const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  .area-imprimible, .area-imprimible * { visibility: visible !important; }
  .area-imprimible { position: absolute; left: 0; top: 0; width: 100%; padding: 16px; color: #000; }
  /* Oculta controles interactivos dentro del documento (botones, inputs, selects) */
  .area-imprimible button, .area-imprimible input, .area-imprimible select, .area-imprimible textarea { display: none !important; }
  .no-print { display: none !important; }
}`;

/** Encabezado de marca: visible solo al imprimir (logo + empresa + título + fecha). */
export function MarcaEmpresa({ titulo, subtitulo }: { titulo?: string; subtitulo?: string }) {
  const { user } = useAuth();
  const emp = user?.empresa;
  return (
    <div className="hidden print:flex items-center gap-4 border-b border-black/30 pb-3 mb-4">
      {emp?.logo ? <img src={emp.logo} alt="" className="h-16 w-16 object-contain" /> : null}
      <div>
        <div className="text-lg font-bold">{emp?.nombre ?? ''}</div>
        {titulo && <div className="text-base">{titulo}</div>}
        {subtitulo && <div className="text-xs text-gray-600">{subtitulo}</div>}
        <div className="text-[11px] text-gray-500">Impreso: {new Date().toLocaleString(LOCALE)}</div>
      </div>
    </div>
  );
}

/** Botón de impresión (se oculta en el documento impreso). */
export function PrintButton({ className = '' }: { className?: string }) {
  return (
    <Button variant="outline" size="sm" className={`no-print ${className}`} onClick={() => window.print()}>
      <Printer className="w-4 h-4 mr-1" /> Imprimir
    </Button>
  );
}

/** Contenedor de lo que se imprime; antepone el encabezado de marca. */
export function Imprimible({ titulo, subtitulo, children }: { titulo?: string; subtitulo?: string; children: ReactNode }) {
  return (
    <div className="area-imprimible">
      <style>{PRINT_CSS}</style>
      <MarcaEmpresa titulo={titulo} subtitulo={subtitulo} />
      {children}
    </div>
  );
}
